// ────────────────────────────────────────────────────────────────────────────
//  Apply.tsx — Öffentliche Bewerbungsseite (/apply/:code)
//
//  Kein Login nötig. Bewerber erhalten die URL aus der Stellenausschreibung
//  (publicUrl im Worker-DTO = https://custosoft.de/apply/<CODE>).
//
//  Three states:
//   1. /apply (ohne Code) → Code-Eingabefeld, dann Redirect zu /apply/:code
//   2. /apply/:code mit gültigem aktiven Job → Stellenanzeige + Form
//   3. Code unbekannt/abgelaufen → Hinweis-Screen
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useT } from '../i18n'
import {
  getPublicJob, submitApplication, uploadApplicationFile,
  type JobLink,
} from '../lib/useRecruitment'
import { BackgroundOrbs } from '../components/BackgroundOrbs'

export function Apply() {
  const { code } = useParams<{ code?: string }>()
  if (!code) return <CodeEntry />
  return <JobView code={code} />
}

// ── Code-Entry (wenn /apply ohne Code) ─────────────────────────────────────
function CodeEntry() {
  const t = useT()
  const nav = useNavigate()
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const c = code.trim().toUpperCase()
    if (!c) return
    setBusy(true); setErr(null)
    try {
      // erst prüfen ob Code valide ist; wenn ja → navigieren
      await getPublicJob(c)
      nav(`/apply/${c}`, { replace: true })
    } catch (e: any) {
      setErr(t('recruit.applyCodeInputErr'))
    } finally { setBusy(false) }
  }

  return (
    <div className="relative min-h-full">
      <BackgroundOrbs />
      <div className="max-w-md mx-auto px-6 py-20">
        <div className="text-center mb-6">
          <div className="text-5xl mb-3">📋</div>
          <h1 className="text-2xl md:text-3xl font-bold">{t('recruit.applyCodeInputTitle')}</h1>
          <p className="text-app-text-dim mt-2">{t('recruit.applyCodeInputSub')}</p>
        </div>
        <form onSubmit={submit} className="glass-strong p-6 space-y-3">
          <input
            type="text"
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
            placeholder={t('recruit.applyCodeInputPh')}
            maxLength={20}
            autoComplete="off"
            className="input-base text-center text-xl font-mono tracking-widest uppercase"
          />
          {err && <div className="text-sm text-coral bg-coral/10 border border-coral/30 rounded-lg p-2.5">{err}</div>}
          <button type="submit" disabled={busy || code.trim().length < 4} className="btn-primary w-full disabled:opacity-50">
            {busy ? '…' : t('recruit.applyCodeInputBtn')}
          </button>
        </form>
      </div>
    </div>
  )
}

// ── Job-View (/apply/:code) ─────────────────────────────────────────────────
function JobView({ code }: { code: string }) {
  const [job, setJob] = useState<JobLink | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<'notfound' | 'expired' | null>(null)

  useEffect(() => {
    setLoading(true)
    getPublicJob(code)
      .then(j => { setJob(j); setLoading(false) })
      .catch(e => {
        // 404 = not found, 410 = expired
        const msg = (e?.message ?? '').toLowerCase()
        if (msg.includes('expired')) setError('expired')
        else                          setError('notfound')
        setLoading(false)
      })
  }, [code])

  if (loading) return <Spinner />
  if (error === 'notfound') return <NotFoundView />
  if (error === 'expired')  return <ExpiredView />
  if (!job) return null

  return <JobApplyView job={job} code={code} />
}

// ── NotFound / Expired States ───────────────────────────────────────────────
function NotFoundView() {
  const t = useT()
  return (
    <div className="relative min-h-full">
      <BackgroundOrbs />
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <div className="text-6xl mb-4">🔍</div>
        <h1 className="text-2xl font-bold">{t('recruit.applyNotFound')}</h1>
        <p className="text-app-text-dim mt-2">{t('recruit.applyNotFoundSub')}</p>
        <Link to="/apply" className="btn-ghost mt-6 inline-flex">{t('recruit.applyCodeInputBtn')}</Link>
      </div>
    </div>
  )
}
function ExpiredView() {
  const t = useT()
  return (
    <div className="relative min-h-full">
      <BackgroundOrbs />
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <div className="text-6xl mb-4">⏳</div>
        <h1 className="text-2xl font-bold">{t('recruit.applyExpired')}</h1>
        <p className="text-app-text-dim mt-2">{t('recruit.applyExpiredSub')}</p>
      </div>
    </div>
  )
}

// ── Bewerbungsformular ──────────────────────────────────────────────────────
interface UploadingFile {
  file: File
  progress: number
  url?: string
  error?: string
}

function JobApplyView({ job, code }: { job: JobLink; code: string }) {
  const t = useT()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [firstName, setFirstName] = useState('')
  const [lastName,  setLastName]  = useState('')
  const [email,     setEmail]     = useState('')
  const [phone,     setPhone]     = useState('')
  const [cover,     setCover]     = useState('')
  const [files,     setFiles]     = useState<UploadingFile[]>([])
  const [busy,      setBusy]      = useState(false)
  const [err,       setErr]       = useState<string | null>(null)
  const [done,      setDone]      = useState(false)
  const [dragActive, setDragActive] = useState(false)

  const onPickFiles = (chosen: FileList | File[]) => {
    const arr = Array.from(chosen)
    arr.forEach(f => {
      if (f.size > 25 * 1024 * 1024) {
        setErr(`„${f.name}" ist größer als 25 MB.`)
        return
      }
      setFiles(prev => [...prev, { file: f, progress: 0 }])
    })
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragActive(false)
    if (e.dataTransfer.files?.length) onPickFiles(e.dataTransfer.files)
  }

  const removeFile = (f: File) =>
    setFiles(prev => prev.filter(x => x.file !== f))

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setErr(null)
    try {
      const r = await submitApplication({
        code,
        firstName: firstName.trim(),
        lastName:  lastName.trim(),
        email:     email.trim(),
        phone:     phone.trim() || undefined,
        coverLetter: cover.trim() || undefined,
      })
      const appId = r.id
      // Anhänge sequenziell hochladen
      for (const f of files) {
        try {
          // mark uploading
          setFiles(prev => prev.map(x => x.file === f.file ? { ...x, progress: 50 } : x))
          const res = await uploadApplicationFile(appId, f.file)
          setFiles(prev => prev.map(x => x.file === f.file ? { ...x, url: res.url, progress: 100 } : x))
        } catch (e: any) {
          setFiles(prev => prev.map(x => x.file === f.file ? { ...x, error: e?.message ?? 'fail' } : x))
        }
      }
      setDone(true)
    } catch (e: any) {
      setErr(e?.message ?? 'failed')
    } finally { setBusy(false) }
  }

  if (done) return <DoneView orgName={job.orgName ?? job.title} onAnother={() => location.reload()} />

  return (
    <div className="relative min-h-full">
      <BackgroundOrbs />
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Hero */}
        <header className="mb-8">
          <div className="inline-block text-[11px] font-bold tracking-wider uppercase text-accent mb-2">
            {t('recruit.applyKicker')}
          </div>
          <h1 className="text-3xl md:text-5xl font-bold leading-tight">{job.title}</h1>
          {job.orgName && (
            <p className="mt-2 text-app-text-dim text-lg">
              {t('recruit.applyAt', { org: job.orgName })}
            </p>
          )}
          {job.description && (
            <div className="mt-6 glass p-5 md:p-6 text-sm md:text-base whitespace-pre-wrap leading-relaxed">
              {job.description}
            </div>
          )}
        </header>

        {/* Form */}
        <section className="glass-strong p-5 md:p-8">
          <h2 className="text-xl md:text-2xl font-bold mb-5">{t('recruit.applyForm')}</h2>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <FormField label={t('recruit.applyFirstName')} required>
                <input type="text" required value={firstName} onChange={e => setFirstName(e.target.value)} maxLength={60} className="input-base" />
              </FormField>
              <FormField label={t('recruit.applyLastName')} required>
                <input type="text" required value={lastName} onChange={e => setLastName(e.target.value)} maxLength={60} className="input-base" />
              </FormField>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <FormField label={t('recruit.applyEmail')} required>
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="input-base" />
              </FormField>
              <FormField label={t('recruit.applyPhone')}>
                <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="input-base" />
              </FormField>
            </div>
            <FormField label={t('recruit.applyCover')}>
              <textarea value={cover} onChange={e => setCover(e.target.value)} placeholder={t('recruit.applyCoverPh')} rows={6} className="input-base" />
            </FormField>

            {/* Anhänge */}
            <FormField label={t('recruit.applyAttachTitle')}>
              <div
                onDragOver={e => { e.preventDefault(); setDragActive(true) }}
                onDragLeave={() => setDragActive(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                className="rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition"
                style={dragActive
                  ? { borderColor: '#8a44ee', background: 'rgba(138,68,238,0.10)' }
                  : { borderColor: 'var(--border-strong)', background: 'var(--bg-input)' }}
              >
                <input ref={fileInputRef} type="file" multiple
                  onChange={e => e.target.files && onPickFiles(e.target.files)}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.txt,image/*" />
                <div className="text-3xl mb-1">📎</div>
                <div className="font-semibold text-sm">{t('recruit.applyDropHere')}</div>
                <div className="text-xs text-app-text-mute mt-1">{t('recruit.applyAttachHint')}</div>
              </div>
              {files.length > 0 && (
                <div className="mt-3 space-y-2">
                  {files.map((f, i) => <FileRow key={i} entry={f} onRemove={() => removeFile(f.file)} t={t} />)}
                </div>
              )}
            </FormField>

            {err && (
              <div className="text-sm text-coral rounded-xl p-3"
                   style={{ background: 'rgba(255,91,92,0.10)', border: '1px solid rgba(255,91,92,0.30)' }}>
                {err}
              </div>
            )}

            <button type="submit" disabled={busy || !firstName.trim() || !lastName.trim() || !email.trim()}
              className="btn-primary w-full text-base py-3.5 disabled:opacity-50">
              {busy ? t('recruit.applySubmitting') : t('recruit.applySubmit')}
            </button>

            <p className="text-[11px] text-app-text-mute text-center leading-relaxed">
              {t('recruit.applyConsent')}
              <a href="https://custosoft-api.davidschroedinger.workers.dev/datenschutz" target="_blank" rel="noreferrer" className="underline">
                {t('recruit.applyConsentLink')}
              </a>.
            </p>
          </form>
        </section>

        <PoweredFooter />
      </div>
    </div>
  )
}

function FileRow({ entry, onRemove, t }: { entry: UploadingFile; onRemove: () => void; t: any }) {
  const isImg = entry.file.type.startsWith('image/')
  const sizeKb = (entry.file.size / 1024).toFixed(0)
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg glass-flat">
      <span className="text-xl">{isImg ? '🖼' : '📄'}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{entry.file.name}</div>
        <div className="text-xs text-app-text-mute">{sizeKb} KB</div>
      </div>
      {entry.error ? (
        <span className="text-xs text-coral font-medium">{t('recruit.applyAttachFail')}</span>
      ) : entry.url ? (
        <span className="text-xs text-teal font-medium">{t('recruit.applyAttachOk')}</span>
      ) : entry.progress > 0 ? (
        <span className="text-xs text-app-text-mute">{t('recruit.applyAttachLoad')}</span>
      ) : null}
      <button type="button" onClick={onRemove}
        className="w-7 h-7 rounded-md text-sm transition"
        style={{ background: 'var(--bg-input)', color: 'var(--text-dim)' }}>×</button>
    </div>
  )
}

// ── Done-View ───────────────────────────────────────────────────────────────
function DoneView({ orgName, onAnother }: { orgName: string; onAnother: () => void }) {
  const t = useT()
  return (
    <div className="relative min-h-full">
      <BackgroundOrbs />
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        <div className="glass-strong p-8 md:p-10">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-2xl md:text-3xl font-bold">{t('recruit.applyDoneTitle')}</h1>
          <p className="text-app-text-dim mt-3 leading-relaxed">
            {t('recruit.applyDoneText', { org: orgName })}
          </p>
          <button onClick={onAnother} className="btn-ghost mt-6">
            {t('recruit.applyDoneAnother')}
          </button>
        </div>
        <PoweredFooter />
      </div>
    </div>
  )
}

// ── Helpers ─────────────────────────────────────────────────────────────────
function FormField(props: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-bold text-app-text-dim mb-1.5 tracking-wider uppercase">
        {props.label}{props.required && <span className="text-coral ml-1">*</span>}
      </span>
      {props.children}
    </label>
  )
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-32">
      <div className="w-10 h-10 rounded-full border-4 border-app-border border-t-accent animate-spin" />
    </div>
  )
}

function PoweredFooter() {
  return (
    <div className="mt-8 text-center text-[11px] text-app-text-mute">
      Powered by{' '}
      <Link to="/" className="font-bold text-app-text-dim hover:text-app-text transition">CustoSoft</Link>
    </div>
  )
}
